import { Component, OnInit, AfterViewInit, Inject, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStep, MatStepper } from '@angular/material/stepper'
import { Store, select } from '@ngrx/store'
import { Channel, Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Connection, stampedSinceLink } from '@tv3/store/connection/connection.model'
import { selectConnectionById } from '@tv3/store/connection/connection.selectors'

@Component({
  selector: 'app-airbnb-listing-wizard',
  templateUrl: './airbnb-listing-wizard.component.html',
  styleUrls: ['./airbnb-listing-wizard.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class AirbnbListingWizardComponent extends Destroyable implements OnInit, AfterViewInit {
  @ViewChild(MatStepper) stepper: MatStepper
  @ViewChild('rates') ratesStep: MatStep

  // Step 1 reported its link → import → availability chain complete in this session. The
  // durable getter below can lag behind that: the link-time connections reload (TV4-6062)
  // may hand the store a server row without the chain's stamps, and a linear mat-stepper
  // silently ignores select() while the previous step's [completed] is false (TV4-5891).
  private connectingStepCompleted = false

  get channel() {
    return this.data.channel
  }

  get connection() {
    return this.data.connection
  }

  // Step completion derived from the durable connection timestamps, so a reopened wizard
  // resumes where the connection actually is instead of restarting from the first step.
  // Only stamps written after the current link count: the room stamps survive an
  // unlink/re-link and would otherwise mark steps done for the previously linked rental.
  // No AVAIL_PUSH_STAMP_FUTURE_SKEW here: unlike BDC, the Airbnb wizard effect writes
  // lastavailpush client-side as plain "now" (never future-dated), and the skew would
  // reject the chain's own just-completed push, freezing the linear stepper on step 1.
  get isConnectingStepDone(): boolean {
    if (this.connectingStepCompleted) {
      return true
    }
    const connection = this.connection
    return !!(
      connection?.rentalId &&
      stampedSinceLink(connection, connection?.lastavailpull) &&
      stampedSinceLink(connection, connection?.lastavailpush)
    )
  }

  get isRatesStepDone(): boolean {
    const connection = this.connection
    return !!connection?.rentalId && stampedSinceLink(connection, connection?.lastratepush)
  }

  constructor(
    public dialogRef: MatDialogRef<AirbnbListingWizardComponent>,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: { channel: Channel; connection: Connection }
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectConnectionById, { id: this.connection.id }), untilDestroy(this)).subscribe((conn) => {
      this.data.connection = conn
    })
  }

  ngAfterViewInit(): void {
    // Resume to the first incomplete step. Deferred a tick so the stepper has rendered
    // and the [completed] bindings are in effect before the linear guard runs.
    setTimeout(() => {
      if (!this.stepper) {
        return
      }
      if (this.isConnectingStepDone && this.isRatesStepDone) {
        this.stepper.selectedIndex = 2
      } else if (this.isConnectingStepDone) {
        this.ratesStep.select()
      }
    })
  }
  close() {
    this.dialogRef.close()
  }

  onChannel() {
    this.dialogRef.close('channel')
  }

  onRatesStep() {
    // Step 1 only emits `next` once its own link/import/availability flags are all set, so
    // the Connecting step is complete for this session whatever the store row says.
    this.connectingStepCompleted = true
    // Deferred a tick: the auto-chain emits `next` synchronously with the store update
    // that completes the Connecting step, and the linear guard would still read the
    // step's previous [completed]="false" binding until change detection runs.
    setTimeout(() => this.ratesStep.select())
  }
}
